import { Link } from "react-router-dom";
import { Anchor, Badge, Group, Text } from "@mantine/core";
import { BookOpen, Check } from "lucide-react";
import { guideStatusMeta } from "@nodus/ui";
import type { MyGuides } from "@/lib/api/guides";
import { sectionGuides } from "@/lib/guides";
import { useMyGuides } from "@/hooks/use-guides";
import { useGuideLink } from "@/hooks/use-guide-modal";

/**
 * Guides pinned to a portal area, in the area page's header (#436): a "Setup
 * guide" link to the first open guide and one chip per guide with its
 * progress. Renders nothing when no guide is placed in the area.
 */
export function SectionGuidesView({ area, data, inset = false }: { area: string; data: MyGuides | undefined; inset?: boolean }) {
  const guideLink = useGuideLink();
  const guides = sectionGuides(area, data);
  if (guides.length === 0) return null;
  const [first] = guides;

  return (
    <Group
      gap="xs"
      wrap="wrap"
      aria-label="Setup guides for this page"
      role="group"
      // ``inset``: its own padded strip, for pages without a padded header (the floorplan).
      {...(inset ? { px: "md", pt: "xs", style: { flexShrink: 0 } } : {})}
    >
      <Anchor component={Link} {...guideLink(first.key)} fz="sm" fw={500}>
        <Group gap={4} wrap="nowrap" component="span">
          <BookOpen size={14} />
          Setup guide
        </Group>
      </Anchor>
      {guides.map((g) => {
        const finished = g.status === "done";
        return (
          <Badge
            key={g.key}
            component={Link}
            {...guideLink(g.key)}
            variant={finished ? "light" : "outline"}
            color={guideStatusMeta(g.status).color === "gray" ? "blue" : guideStatusMeta(g.status).color}
            radius="sm"
            size="lg"
            tt="none"
            fw={500}
            leftSection={finished ? <Check size={12} /> : undefined}
            style={{ cursor: "pointer" }}
            aria-label={`${g.title}: ${finished ? "finished" : `${g.done} of ${g.total} steps`}`}
          >
            {g.title}
            {!finished && (
              <Text component="span" fz="xs" c="dimmed" ml={6}>
                {g.done}/{g.total}
              </Text>
            )}
          </Badge>
        );
      })}
    </Group>
  );
}

export function SectionGuides({ area, inset }: { area: string; inset?: boolean }) {
  const { data } = useMyGuides();
  return <SectionGuidesView area={area} data={data} inset={inset} />;
}
